import { useCallback, useEffect, useId, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useAuth } from '../auth/AuthContext';
import { isAssetOwnerLoginType, isBusinessClientLogin, canBookAssets } from '../auth/userLoginType';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { fetchProfile, patchProfile, postChangePassword } from '../api/profileApi';
import { AssetRequestError } from '../api/getAssetById';
import { useI18n } from '../i18n';
import pageThemeStyles from './menuPageTheme.module.css';
import styles from './ProfileMenu.module.css';
import { ProfileAvatarCropPanel } from './ProfileAvatarCropPanel';
import { ProfileAvatarImage } from './ProfileAvatarImage';
import { BirthDateSplitPicker } from './BirthDateSplitPicker';
import { PasswordInputRow } from './PasswordInputRow';
import {
  extractProfileDisplaySummary,
  type ProfileDisplaySummary,
} from '../utils/profileDisplaySummary';
import { resolveProfileAvatarDisplay } from '../utils/profileAvatarDisplay';
import { loadLocalProfileAvatarSrc, saveLocalProfileAvatarSrc } from '../utils/profileAvatarStorage';
import { buildProfileEditPatchPayload } from '../utils/profileAvatarUpload';
import { OWNER_LOGO_PICK_MAX_FILE_BYTES, ownerLogoDataUrlWithinUploadBudget, prepareOwnerLogoDataUrlForUpload } from '../utils/profileOwnerLogoUpload';
import { mergeSavedProfileEditFields, persistProfileEditSavedFields, patchSignedInProfileEdit, buildSignedInProfileEditPatchBody } from '../utils/profileEditSave';
import { buildAssetOwnerProfileEditPatchBody } from '../utils/profileAssetOwnerEdit';
import { persistLocalBookingProfileFromWire } from '../utils/profileBookingCompleteStorage';
import { assertProfileMutationSuccess } from '../utils/profileWorkerList';
import { formatProfileLastUpdatedAt } from '../utils/profileLastUpdated';
import { phoneForProfileEditInput, formatIsraeliPhoneInput } from '../utils/phoneE164';
import {
  clearPendingBusinessClientLogo,
  getPendingBusinessClientLogo,
} from '../utils/businessClientPendingLogoStorage';
import {
  normalizeProfileVehiclesPrimary,
  profileVehiclesToWire,
  validateProfileVehicles,
  type ProfileVehicle,
} from '../utils/profileVehicles';
import { clearStoredProfileVehicles } from '../utils/profileVehiclesStorage';
import { ProfileVehiclesEditSection } from './ProfileVehiclesEditSection';

export type ProfileEditPanelProps = {
  onBack: () => void;
  onProfileLoaded?: (summary: ProfileDisplaySummary) => void;
  onSaved?: (summary: ProfileDisplaySummary) => void;
  onAvatarUpdated?: (avatarSrc: string) => void;
};

export function ProfileEditPanel({ onBack, onProfileLoaded, onSaved, onAvatarUpdated }: ProfileEditPanelProps) {
  const { t, language } = useI18n();
  const panelId = useId();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const ownerLogoInputRef = useRef<HTMLInputElement>(null);
  const { userName, userLoginType } = useAuth();
  const assetOwnerProfile = isAssetOwnerLoginType(userLoginType);
  const businessClientProfile = isBusinessClientLogin(userLoginType);
  const businessStyleProfile = assetOwnerProfile || businessClientProfile;
  const showVehiclesSection = canBookAssets(userLoginType);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessId, setBusinessId] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumber2, setPhoneNumber2] = useState('');
  const [email, setEmail] = useState('');
  const [occupation, setOccupation] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [readOnlySummary, setReadOnlySummary] = useState<ProfileDisplaySummary | null>(null);
  const [avatarPreviewSrc, setAvatarPreviewSrc] = useState<string | null>(null);
  const [pendingAvatarDataUrl, setPendingAvatarDataUrl] = useState<string | null>(null);
  const [avatarCropFile, setAvatarCropFile] = useState<File | null>(null);
  const [ownerLogoPreviewSrc, setOwnerLogoPreviewSrc] = useState<string | null>(null);
  const [pendingOwnerLogoDataUrl, setPendingOwnerLogoDataUrl] = useState<string | null>(null);
  const [vehicles, setVehicles] = useState<ProfileVehicle[]>([]);
  const onProfileLoadedRef = useRef(onProfileLoaded);
  onProfileLoadedRef.current = onProfileLoaded;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProfile();
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        setReadOnlySummary(summary);
        onProfileLoadedRef.current?.(summary);
        setFirstName(summary.firstName ?? '');
        setLastName(summary.lastName ?? '');
        setBusinessName(summary.businessName ?? '');
        setBusinessId(summary.businessId ?? '');
        setPhoneNumber(phoneForProfileEditInput(summary.phone));
        setPhoneNumber2(phoneForProfileEditInput(summary.phoneSecondary));
        setEmail(summary.email ?? '');
        setOccupation(summary.occupation ?? '');
        setBirthDate(summary.dateOfBirth ?? '');
        setAvatarPreviewSrc(resolveProfileAvatarDisplay(summary.avatarSrc));
        setPendingAvatarDataUrl(null);
        setOwnerLogoPreviewSrc(summary.ownerLogoSrc);
        setPendingOwnerLogoDataUrl(null);
        setVehicles(summary.vehicles ?? []);
        if (businessClientProfile) {
          const pendingLogo = getPendingBusinessClientLogo(getStoredLoginIdentifier());
          if (pendingLogo && !summary.ownerLogoSrc) {
            setOwnerLogoPreviewSrc(pendingLogo);
            setPendingOwnerLogoDataUrl(pendingLogo);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('common.somethingWrong'));
          setAvatarPreviewSrc(resolveProfileAvatarDisplay(null));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [t, userName, businessClientProfile]);

  const onAvatarPick = useCallback(() => {
    avatarInputRef.current?.click();
  }, []);

  const onAvatarFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    setAvatarCropFile(file);
  }, []);

  const onAvatarCropCancel = useCallback(() => {
    setAvatarCropFile(null);
  }, []);

  const onAvatarCropSave = useCallback((dataUrl: string) => {
    setAvatarCropFile(null);
    setPendingAvatarDataUrl(dataUrl);
    setAvatarPreviewSrc(dataUrl);
  }, []);

  const onOwnerLogoPick = useCallback(() => {
    ownerLogoInputRef.current?.click();
  }, []);

  const onOwnerLogoFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !file.type.startsWith('image/')) {
      return;
    }
    if (file.size > OWNER_LOGO_PICK_MAX_FILE_BYTES) {
      setError(t('register.businessLogoTooLarge'));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      void (async () => {
        if (typeof reader.result !== 'string') {
          return;
        }
        try {
          const compressed = await prepareOwnerLogoDataUrlForUpload(reader.result);
          if (!ownerLogoDataUrlWithinUploadBudget(compressed)) {
            setError(t('register.businessLogoTooLargePayload'));
            return;
          }
          setPendingOwnerLogoDataUrl(compressed);
          setOwnerLogoPreviewSrc(compressed);
          setError(null);
        } catch {
          setError(t('register.businessLogoTooLargePayload'));
        }
      })();
    };
    reader.readAsDataURL(file);
  }, [t]);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setSaving(true);
      setError(null);
      try {
        const oldPw = oldPassword.trim();
        const newPw = newPassword.trim();
        const confirmPw = confirmPassword.trim();
        const passwordMismatch =
          Boolean(newPw) && Boolean(confirmPw) && newPw !== confirmPw;
        if (passwordMismatch) {
          throw new Error(t('profile.passwordMismatch'));
        }
        const readyToChangePassword = Boolean(oldPw && newPw && confirmPw);
        if (readyToChangePassword) {
          if (newPw !== confirmPw) {
            throw new Error(t('profile.passwordMismatch'));
          }
          const passwordResponse = await postChangePassword({ old_password: oldPw, new_password: newPw });
          assertProfileMutationSuccess(passwordResponse, t('profile.operation.changePassword'));
          setOldPassword('');
          setNewPassword('');
          setConfirmPassword('');
        }

        const normalizedVehicles = showVehiclesSection
          ? normalizeProfileVehiclesPrimary(vehicles)
          : [];
        if (showVehiclesSection) {
          const vehiclesIssue = validateProfileVehicles(normalizedVehicles);
          if (vehiclesIssue) {
            const messageKey =
              vehiclesIssue === 'max'
                ? 'profile.vehicles.errorMax'
                : vehiclesIssue === 'license'
                  ? 'profile.vehicles.errorLicense'
                  : vehiclesIssue === 'duplicateLicense'
                    ? 'profile.vehicles.errorDuplicateLicense'
                    : vehiclesIssue === 'primary'
                      ? 'profile.vehicles.errorPrimary'
                      : 'profile.vehicles.errorIncomplete';
            throw new Error(t(messageKey));
          }
        }

        let logoForPatch: string | undefined;
        if (businessStyleProfile && pendingOwnerLogoDataUrl?.startsWith('data:image/')) {
          logoForPatch = await prepareOwnerLogoDataUrlForUpload(pendingOwnerLogoDataUrl);
        }

        let profileImageForPatch: string | undefined;
        if (pendingAvatarDataUrl?.startsWith('data:image/')) {
          const avatarPayload = await buildProfileEditPatchPayload(
            firstName,
            lastName,
            occupation,
            birthDate,
            pendingAvatarDataUrl,
            null,
          );
          const raw = avatarPayload.profile_image;
          if (typeof raw === 'string' && raw.trim()) {
            profileImageForPatch = raw.trim();
          }
        }

        const payload = businessStyleProfile
          ? buildAssetOwnerProfileEditPatchBody(
              {
                businessName,
                businessId,
                email,
                contactFirstName: firstName,
                contactLastName: lastName,
                phoneNumber,
                phoneNumber2,
              },
              { logo: logoForPatch, profileImage: profileImageForPatch },
            )
          : buildSignedInProfileEditPatchBody({
              firstName,
              lastName,
              occupation,
              birthDate,
              profileImage: profileImageForPatch,
              phoneNumber,
              ...(showVehiclesSection && normalizedVehicles.length > 0
                ? { vehicles: profileVehiclesToWire(normalizedVehicles) }
                : {}),
            });

        if (businessStyleProfile) {
          const payloadWithVehicles = showVehiclesSection
            ? {
                ...payload,
                vehicles: profileVehiclesToWire(normalizedVehicles),
              }
            : payload;
          const patchResponse = await patchProfile(payloadWithVehicles);
          assertProfileMutationSuccess(patchResponse, t('profile.operation.patchProfile'));
        } else {
          await patchSignedInProfileEdit(payload);
        }

        const refreshed = await fetchProfile();
        const savedFields = {
          firstName,
          lastName,
          occupation,
          birthDate,
          vehicles: showVehiclesSection ? normalizedVehicles : [],
        };
        persistProfileEditSavedFields(savedFields);
        if (showVehiclesSection) {
          clearStoredProfileVehicles(getStoredLoginIdentifier());
        }
        if (businessClientProfile && (logoForPatch || pendingOwnerLogoDataUrl)) {
          clearPendingBusinessClientLogo(getStoredLoginIdentifier());
        }
        const summary = mergeSavedProfileEditFields(
          extractProfileDisplaySummary(
            refreshed,
            userName,
            getStoredLoginIdentifier(),
            getSessionToken(),
          ),
          savedFields,
        );
        persistLocalBookingProfileFromWire({
          first_name: firstName,
          last_name: lastName,
          occupation,
          birth_date: birthDate,
          email: email.trim() || summary.email || getStoredLoginIdentifier(),
          phone_number: phoneNumber,
        });
        setReadOnlySummary(summary);
        setVehicles(summary.vehicles);
        const displayAvatar = resolveProfileAvatarDisplay(summary.avatarSrc, pendingAvatarDataUrl);
        if (pendingAvatarDataUrl || summary.avatarSrc) {
          saveLocalProfileAvatarSrc(displayAvatar);
          onAvatarUpdated?.(displayAvatar);
        }
        onSaved?.({ ...summary, avatarSrc: summary.avatarSrc ?? pendingAvatarDataUrl });
        onBack();
      } catch (err) {
        setError(
          err instanceof AssetRequestError
            ? err.message
            : err instanceof Error
              ? err.message
              : t('common.somethingWrong'),
        );
      } finally {
        setSaving(false);
      }
    },
    [businessStyleProfile, birthDate, businessId, businessName, confirmPassword, email, firstName, lastName, newPassword, occupation, oldPassword, onBack, onAvatarUpdated, onSaved, pendingAvatarDataUrl, pendingOwnerLogoDataUrl, phoneNumber, phoneNumber2, showVehiclesSection, t, userName, vehicles, businessClientProfile],
  );

  const textDir = language === 'he' ? 'rtl' : 'ltr';
  const avatarSrc = avatarPreviewSrc ?? resolveProfileAvatarDisplay(readOnlySummary?.avatarSrc, loadLocalProfileAvatarSrc());
  const passwordMismatch =
    Boolean(newPassword.trim()) &&
    Boolean(confirmPassword.trim()) &&
    newPassword.trim() !== confirmPassword.trim();
  const profileUpdatedLabel = useMemo(() => {
    const formatted = formatProfileLastUpdatedAt(readOnlySummary?.updatedAtIso, language);
    return formatted
      ? t('profile.hub.profileLastUpdated', { date: formatted })
      : t('profile.hub.profileLastUpdatedUnknown');
  }, [readOnlySummary?.updatedAtIso, language, t]);
  const onPhoneInput = (setter: (value: string) => void) => (value: string) => {
    setter(formatIsraeliPhoneInput(value));
  };

  return (
    <div
      className={[pageThemeStyles.wrapEmbedded, pageThemeStyles.wrapThemed].join(' ')}
      role="region"
      aria-label={t('profile.hub.editProfile')}
    >
      <div className={[pageThemeStyles.panelEmbedded, pageThemeStyles.browsePage].join(' ')}>
        <div className={styles.hubSubPanel} dir={textDir}>
          <div className={styles.hubTopBar}>
            <button type="button" className={styles.hubTopBarBtn} onClick={onBack} aria-label={t('profile.hub.back')}>
              <ProfileBackIcon rtl={language === 'he'} />
            </button>
            <h2 className={styles.hubTopBarTitle}>{t('profile.hub.editProfile')}</h2>
            <span className={styles.hubTopBarSpacer} aria-hidden />
          </div>

          {!loading ? (
            <p className={styles.hubDetailsProfileUpdatedStamp} aria-label={profileUpdatedLabel}>
              {profileUpdatedLabel}
            </p>
          ) : null}

          {loading ? (
            <p className={pageThemeStyles.browseEmpty} role="status">
              {t('common.loading')}
            </p>
          ) : (
            <form className={styles.profileEditForm} dir={textDir} onSubmit={(event) => void onSubmit(event)}>
              <div className={styles.profileEditAvatarBlock}>
                <div className={styles.profileEditAvatarWrap}>
                  <ProfileAvatarImage src={avatarSrc} className={styles.profileEditAvatar} />
                  <button
                    type="button"
                    className={styles.hubAvatarCameraBtn}
                    aria-label={t('profile.hub.changePhotoAria')}
                    title={t('profile.hub.changePhoto')}
                    onClick={onAvatarPick}
                    disabled={saving}
                  >
                    <ProfileCameraIcon />
                  </button>
                  <input
                    ref={avatarInputRef}
                    type="file"
                    accept="image/*"
                    className={styles.hubAvatarFileInput}
                    tabIndex={-1}
                    aria-hidden
                    onChange={onAvatarFileChange}
                  />
                </div>
              </div>

              {businessStyleProfile ? (
                <>
                  <ProfileEditField
                    id={`${panelId}-business-name`}
                    label={t('assetField.businessName')}
                    value={businessName}
                    onChange={setBusinessName}
                    disabled={saving}
                  />
                  <div className={styles.profileEditOwnerLogoBlock}>
                    <span className={styles.profileEditOwnerLogoLabel}>{t('register.businessLogo')}</span>
                    <input
                      ref={ownerLogoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className={styles.hubAvatarFileInput}
                      tabIndex={-1}
                      aria-hidden
                      onChange={onOwnerLogoFileChange}
                    />
                    {ownerLogoPreviewSrc ? (
                      <div className={styles.profileEditOwnerLogoPreviewRow}>
                        <img
                          src={ownerLogoPreviewSrc}
                          alt=""
                          className={styles.profileEditOwnerLogoImg}
                        />
                        <button
                          type="button"
                          className={styles.profileEditOwnerLogoChangeBtn}
                          onClick={onOwnerLogoPick}
                          disabled={saving}
                        >
                          {t('register.businessLogoChange')}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className={styles.profileEditOwnerLogoAddBtn}
                        onClick={onOwnerLogoPick}
                        disabled={saving}
                      >
                        {t('register.businessLogoAdd')}
                      </button>
                    )}
                  </div>
                  <ProfileEditField
                    id={`${panelId}-business-id`}
                    label={t('assetField.businessId')}
                    value={businessId}
                    onChange={setBusinessId}
                    disabled={saving}
                  />
                  <ProfileEditField
                    id={`${panelId}-email`}
                    label={t('profile.field.email')}
                    value={email}
                    onChange={setEmail}
                    disabled={saving}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    forceLtr
                  />
                  <ProfileEditField
                    id={`${panelId}-first-name`}
                    label={t('profile.field.firstName')}
                    value={firstName}
                    onChange={setFirstName}
                    disabled={saving}
                  />
                  <ProfileEditField
                    id={`${panelId}-last-name`}
                    label={t('profile.field.lastName')}
                    value={lastName}
                    onChange={setLastName}
                    disabled={saving}
                  />
                  <ProfileEditField
                    id={`${panelId}-phone`}
                    label={t('profile.field.cellPhone')}
                    value={phoneNumber}
                    onChange={onPhoneInput(setPhoneNumber)}
                    disabled={saving}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    forceLtr
                  />
                  <ProfileEditField
                    id={`${panelId}-phone-2`}
                    label={t('assetField.phoneNumber2')}
                    value={phoneNumber2}
                    onChange={onPhoneInput(setPhoneNumber2)}
                    disabled={saving}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    forceLtr
                  />
                </>
              ) : (
                <>
                  <ProfileEditField
                    id={`${panelId}-first-name`}
                    label={t('profile.field.firstName')}
                    value={firstName}
                    onChange={setFirstName}
                    disabled={saving}
                  />
                  <ProfileEditField
                    id={`${panelId}-last-name`}
                    label={t('profile.field.lastName')}
                    value={lastName}
                    onChange={setLastName}
                    disabled={saving}
                  />
                  <ProfileEditField
                    id={`${panelId}-phone`}
                    label={t('profile.field.cellPhone')}
                    value={phoneNumber}
                    onChange={onPhoneInput(setPhoneNumber)}
                    disabled={saving}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    forceLtr
                  />
                  <ProfileEditField
                    id={`${panelId}-email`}
                    label={t('profile.field.email')}
                    value={email}
                    onChange={setEmail}
                    disabled={saving}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    forceLtr
                  />
                  <div className={styles.profileEditField}>
                    <span className={styles.profileEditLabel} id={`${panelId}-dob-label`}>
                      {t('profile.field.dateOfBirth')}
                    </span>
                    <BirthDateSplitPicker
                      id={`${panelId}-dob`}
                      value={birthDate}
                      onChange={setBirthDate}
                      disabled={saving}
                      theme="light"
                      showFieldLabel={false}
                    />
                  </div>
                  <ProfileEditField
                    id={`${panelId}-occupation`}
                    label={t('profile.field.occupation')}
                    value={occupation}
                    onChange={setOccupation}
                    disabled={saving}
                  />
                </>
              )}

              {showVehiclesSection ? (
                <ProfileVehiclesEditSection
                  vehicles={vehicles}
                  onChange={setVehicles}
                  disabled={saving}
                />
              ) : null}

              <section className={styles.profileEditPasswordSection} aria-labelledby={`${panelId}-pw-heading`}>
                <h3 id={`${panelId}-pw-heading`} className={styles.profileEditPasswordTitle}>
                  {t('profile.operation.changePassword')}
                </h3>
                <p className={styles.profileEditPasswordOptional}>{t('profile.passwordChangeOptional')}</p>
                <ProfileEditPasswordField
                  id={`${panelId}-current-pw`}
                  label={t('profile.currentPassword')}
                  value={oldPassword}
                  onChange={setOldPassword}
                  disabled={saving}
                />
                <ProfileEditPasswordField
                  id={`${panelId}-new-pw`}
                  label={t('profile.newPassword')}
                  value={newPassword}
                  onChange={setNewPassword}
                  disabled={saving}
                />
                <ProfileEditPasswordField
                  id={`${panelId}-confirm-pw`}
                  label={t('profile.confirmNewPassword')}
                  value={confirmPassword}
                  onChange={setConfirmPassword}
                  disabled={saving}
                  ariaInvalid={passwordMismatch}
                  ariaDescribedBy={passwordMismatch ? `${panelId}-pw-mismatch` : undefined}
                />
                {passwordMismatch ? (
                  <p id={`${panelId}-pw-mismatch`} className={styles.profileEditPasswordHint} role="alert">
                    {t('profile.passwordMismatch')}
                  </p>
                ) : null}
              </section>

              {error ? (
                <p className={styles.profileEditError} role="alert">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                className={styles.profileEditSaveBtn}
                disabled={saving}
              >
                {saving ? t('common.submitting') : t('profile.edit.save')}
              </button>
            </form>
          )}
        </div>
      </div>
      {avatarCropFile ? (
        <ProfileAvatarCropPanel file={avatarCropFile} onCancel={onAvatarCropCancel} onSave={onAvatarCropSave} />
      ) : null}
    </div>
  );
}

function ProfileEditField({
  id,
  label,
  value,
  onChange,
  disabled,
  type = 'text',
  inputMode,
  autoComplete,
  forceLtr = false,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  type?: string;
  inputMode?: 'text' | 'tel' | 'email' | 'numeric';
  autoComplete?: string;
  forceLtr?: boolean;
}) {
  return (
    <div className={styles.profileEditField}>
      <label className={styles.profileEditLabel} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={[styles.profileEditInput, forceLtr ? styles.profileEditInputLtr : ''].filter(Boolean).join(' ')}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        dir={forceLtr ? 'ltr' : undefined}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
}

function ProfileEditPasswordField({
  id,
  label,
  value,
  onChange,
  disabled,
  ariaInvalid,
  ariaDescribedBy,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
}) {
  return (
    <div className={styles.profileEditField}>
      <label className={styles.profileEditLabel} htmlFor={id}>
        {label}
      </label>
      <PasswordInputRow
        id={id}
        value={value}
        onChange={onChange}
        inputClassName={[styles.profileEditInput, styles.profileEditInputLtr].join(' ')}
        rowClassName={styles.profileEditPasswordRow}
        autoComplete="off"
        disabled={disabled}
        forceLtr
        ariaInvalid={ariaInvalid}
        ariaDescribedBy={ariaDescribedBy}
      />
    </div>
  );
}

function ProfileBackIcon({ rtl }: { rtl: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d={rtl ? 'M15 6l-6 6 6 6' : 'M9 6l6 6-6 6'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ProfileCameraIcon() {
  return (
    <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}
